// Port of SuiteModel/NameGenerator.swift and NameLists.swift: Scrivener's name generator in the story model's terms.
// Surname first, so alliteration picks a matching forename; double-barrelled joins two surnames; initials "J. R. Pigott";
// forename and surname filters; Polish surnames take their feminine forms for women.
//
// Determinism: given the same random source, the same names come out as on the Mac. The source is injected; it yields
// 64-bit values (as Swift's `RandomNumberGenerator.next()`), and every draw goes through Swift's own
// `randomElement(using:)` → `next(upperBound:)` (Lemire's nearly divisionless method), so a SplitMix64 seeded alike on
// both sides draws alike.
import { graphemes, charIsLetter, trimCFWhitespace } from '../unicode.ts';
import { BUILT_IN_ORIGINS, BUILT_IN_SOURCES, DEFAULT_LIST_IDS } from './nameLists.ts';
import { randomElement, systemRandom, type RandomSource } from '../random.ts';

export { BUILT_IN_ORIGINS, DEFAULT_LIST_IDS };

// MARK: Random numbers (random.ts, kept here for the imports that name them so)

export { nextBelow, randomElement, SplitMix64, systemRandom, type RandomSource } from '../random.ts';

// MARK: Lists

export type NameListKind = 'female' | 'male' | 'anyone' | 'surnames';

export interface NameList {
  id: string;
  /** Where the names come from ("British", "Italian", "Literary"); built-in lists share it across kinds. */
  origin: string;
  kind: NameListKind;
  names: string[];
  /** How a surname changes for a woman, where the language does that. */
  feminine?: 'polish';
}

export const NAME_LIST_KIND_LABELS: Record<NameListKind, string> = {
  female: "Women's forenames", male: "Men's forenames", anyone: 'Forenames (anyone)', surnames: 'Surnames',
};

/** `NameList.title`. */
export const nameListTitle = (l: NameList) =>
  `${l.origin} ${l.kind === 'surnames' ? 'surnames' : l.kind === 'female' ? "women's forenames" : l.kind === 'male' ? "men's forenames" : 'forenames'}`;

const trimSpaces = (s: string) => trimCFWhitespace(s);

/** `NameList.parse`: one name per line (or separated by commas), blank lines and `#` comments skipped, repeats dropped
 *  (ignoring case), the order kept. */
export function parseNames(text: string): string[] {
  const seen = new Set<string>();
  const names: string[] = [];
  for (const line of text.split(/\r\n|[\n\r\u000b\u000c\u0085\u2028\u2029]/)) {
    if (trimSpaces(line).startsWith('#')) continue;
    for (const part of line.split(',')) {
      const name = trimSpaces(part);
      if (name === '' || seen.has(name.toLowerCase())) continue;
      seen.add(name.toLowerCase());
      names.push(name);
    }
  }
  return names;
}

/** `NameList.builtIn`. */
export const BUILT_IN_LISTS: readonly NameList[] = BUILT_IN_SOURCES.map((s) => ({
  id: s.id, origin: s.origin, kind: s.kind, names: parseNames(s.text), ...(s.feminine ? { feminine: s.feminine } : {}),
}));

/** `NameList.common`: the first names of the list for an obscurity level (1 = the most common tenth, 10 = all of it;
 *  at least fifteen). */
export function commonNames(list: NameList, level: number): string[] {
  const l = Math.min(10, Math.max(1, level));
  if (l >= 10) return list.names;
  const share = Math.ceil((list.names.length * l) / 10);
  return list.names.slice(0, Math.max(Math.min(list.names.length, 15), share));
}

/** `NameList.feminine(of:)`: Kowalski → Kowalska, Konieczny → Konieczna. */
export function feminineOf(list: NameList, surname: string): string {
  if (list.feminine === 'polish' && (surname.endsWith('ki') || surname.endsWith('ny'))) {
    const g = graphemes(surname);
    return g.slice(0, -1).join('') + 'a';
  }
  return surname;
}

// MARK: Options

export type NameGender = 'female' | 'male' | 'either';
export type FilterMode = 'any' | 'exactly' | 'startsWith' | 'endsWith' | 'contains';
export interface NameFilter { mode: FilterMode; text: string }

export interface NameOptions {
  gender: NameGender;
  alliterate: boolean;
  doubleBarrelled: boolean;
  initialsOnly: boolean;
  /** 1…3. */
  forenameCount: number;
  forename: NameFilter;
  surname: NameFilter;
  /** 1 (only the most common names) … 10 (every name in the lists). */
  obscurity: number;
  listIDs: string[];
  count: number;
}

export const defaultNameOptions = (): NameOptions => ({
  gender: 'either', alliterate: false, doubleBarrelled: false, initialsOnly: false, forenameCount: 1,
  forename: { mode: 'any', text: '' }, surname: { mode: 'any', text: '' }, obscurity: 6, listIDs: [...DEFAULT_LIST_IDS], count: 50,
});

/** `NameOptions(from:)`: tolerates keys added later and missing ones; clamps the numbers. */
export function decodeNameOptions(json: unknown): NameOptions {
  const o = (json && typeof json === 'object' ? json : {}) as Record<string, unknown>;
  const d = defaultNameOptions();
  const int = (v: unknown, fallback: number) => (typeof v === 'number' && Number.isInteger(v) ? v : fallback);
  const filter = (v: unknown): NameFilter => {
    const f = v as Record<string, unknown> | null;
    const modes: FilterMode[] = ['any', 'exactly', 'startsWith', 'endsWith', 'contains'];
    return f && typeof f === 'object' && modes.includes(f.mode as FilterMode) && typeof f.text === 'string'
      ? { mode: f.mode as FilterMode, text: f.text } : { mode: 'any', text: '' };
  };
  return {
    gender: ['female', 'male', 'either'].includes(o.gender as string) ? (o.gender as NameGender) : 'either',
    alliterate: typeof o.alliterate === 'boolean' ? o.alliterate : d.alliterate,
    doubleBarrelled: typeof o.doubleBarrelled === 'boolean' ? o.doubleBarrelled : d.doubleBarrelled,
    initialsOnly: typeof o.initialsOnly === 'boolean' ? o.initialsOnly : d.initialsOnly,
    forenameCount: Math.min(3, Math.max(1, int(o.forenameCount, 1))),
    forename: filter(o.forename),
    surname: filter(o.surname),
    obscurity: Math.min(10, Math.max(1, int(o.obscurity, 6))),
    listIDs: Array.isArray(o.listIDs) && o.listIDs.every((x) => typeof x === 'string') ? [...new Set(o.listIDs as string[])] : [...DEFAULT_LIST_IDS],
    count: Math.min(500, Math.max(1, int(o.count, 50))),
  };
}

/** `NameGenerator.fold`: lowercased without accents, for comparing ("Łukasz" starts with "l"). */
export function foldName(text: string): string {
  return text.replaceAll('ł', 'l').replaceAll('Ł', 'L').normalize('NFD').replace(/\p{Mn}/gu, '').toLowerCase().normalize('NFC');
}

/** `NameGenerator.initial(of:)`: the letter a name alliterates on and its initial is written with (accents kept). */
export function initialOf(name: string): string {
  const c = graphemes(name).find(charIsLetter);
  return c === undefined ? '' : c.toUpperCase();
}

/** Whether two names alliterate (accents ignored: Émile and Evans do). */
export const alliterates = (a: string, b: string) => foldName(initialOf(a)) === foldName(initialOf(b));

const isActive = (f: NameFilter) => f.mode !== 'any' && trimSpaces(f.text) !== '';

function filterMatches(f: NameFilter, name: string): boolean {
  if (!isActive(f)) return true;
  const a = foldName(name), b = foldName(trimSpaces(f.text));
  switch (f.mode) {
    case 'any': return true;
    case 'exactly': return a === b;
    case 'startsWith': return a.startsWith(b);
    case 'endsWith': return a.endsWith(b);
    case 'contains': return a.includes(b);
  }
}

function describe(f: NameFilter): string {
  const text = `“${trimSpaces(f.text)}”`;
  switch (f.mode) {
    case 'any': case 'exactly': return `is ${text}`;
    case 'startsWith': return `starts with ${text}`;
    case 'endsWith': return `ends with ${text}`;
    case 'contains': return `contains ${text}`;
  }
}

// MARK: Generating

export interface GeneratedName { forenames: string[]; surname: string; gender: NameGender; initialsOnly: boolean }

/** `GeneratedName.full`. */
export function fullName(n: GeneratedName): string {
  const first = n.initialsOnly ? n.forenames.map((f) => initialOf(f) + '.') : n.forenames;
  return [...first, n.surname].filter((s) => s !== '').join(' ');
}

export interface NameResult {
  names: GeneratedName[];
  /** Why fewer names than asked for (or none) came out. */
  note: string | null;
}

/** `NameGenerator.generate`. */
export function generateNames(options: NameOptions, lists: readonly NameList[] = BUILT_IN_LISTS, rng: RandomSource = systemRandom): NameResult {
  const ids = new Set(options.listIDs);
  const chosen = lists.filter((l) => ids.has(l.id));
  // The surname pool keeps each name's list, for surnames that change for women.
  const surnames = chosen.filter((l) => l.kind === 'surnames').flatMap((list) => commonNames(list, options.obscurity).map((name) => ({ name, list })));
  const forenames = (gender: 'female' | 'male') => {
    const seen = new Set<string>();
    return chosen.filter((l) => l.kind === 'anyone' || l.kind === gender).flatMap((l) => commonNames(l, options.obscurity))
      .filter((n) => !seen.has(n) && (seen.add(n), true));
  };
  const pools = new Map<NameGender, string[]>([['female', forenames('female')], ['male', forenames('male')]]);
  const genders: NameGender[] = options.gender === 'either'
    ? (['female', 'male'] as NameGender[]).filter((g) => (pools.get(g) ?? []).length > 0) : [options.gender];

  const fixedForename = options.forename.mode === 'exactly' && isActive(options.forename);
  const fixedSurname = options.surname.mode === 'exactly' && isActive(options.surname);
  const firstPools = new Map<NameGender, string[]>([...pools].map(([g, p]) => [g, p.filter((n) => filterMatches(options.forename, n))]));
  const surnamePool = surnames.filter((s) => filterMatches(options.surname, s.name));
  // Forenames by the letter they alliterate on, folded once rather than per attempt.
  const byInitial = new Map<NameGender, Map<string, string[]>>();
  if (options.alliterate) {
    for (const [g, pool] of firstPools) {
      const groups = new Map<string, string[]>();
      for (const n of pool) { const k = foldName(initialOf(n)); groups.set(k, [...(groups.get(k) ?? []), n]); }
      byInitial.set(g, groups);
    }
  }

  // What's missing, in the writer's terms.
  if (!fixedForename) {
    if (!genders.length || genders.every((g) => (pools.get(g) ?? []).length === 0)) {
      return { names: [], note: `Include a forename list${options.gender === 'female' ? ' for women' : options.gender === 'male' ? ' for men' : ''}.` };
    }
    if (genders.every((g) => (firstPools.get(g) ?? []).length === 0)) {
      return { names: [], note: `No forename in the chosen lists ${describe(options.forename)}.` };
    }
  }
  if (!fixedSurname) {
    if (!surnames.length) return { names: [], note: 'Include a surname list.' };
    if (!surnamePool.length) return { names: [], note: `No surname in the chosen lists ${describe(options.surname)}.` };
  }

  const names: GeneratedName[] = [];
  const seen = new Set<string>();
  let alliterationMissed = false;
  const target = Math.min(500, Math.max(1, options.count));
  let attempts = 0;
  while (names.length < target && attempts < target * 40) {
    attempts += 1;
    const gender = randomElement(genders, rng) ?? 'female';
    if (!(fixedForename || (firstPools.get(gender) ?? []).length > 0)) continue;

    // Surname first: alliteration then looks for a forename to match it.
    let surname: string;
    const family: { name: string; list: NameList }[] = [];
    if (fixedSurname) {
      surname = trimSpaces(options.surname.text);
    } else {
      const pick = randomElement(surnamePool, rng);
      if (!pick) break;
      family.push(pick);
      surname = pick.name;
    }
    let first: string;
    if (fixedForename) {
      first = trimSpaces(options.forename.text);
    } else {
      const pool = firstPools.get(gender) ?? [];
      if (options.alliterate) {
        const matching = byInitial.get(gender)?.get(foldName(initialOf(surname))) ?? [];
        if (!matching.length) {
          // Try another surname first; late on, settle for a name that doesn't alliterate.
          if (!(attempts > target * 20)) continue;
          const any = randomElement(pool, rng);
          if (any === null) continue;
          alliterationMissed = true;
          first = any;
        } else {
          first = randomElement(matching, rng)!;
        }
      } else {
        first = randomElement(pool, rng)!;
      }
    }
    if (!fixedSurname && options.doubleBarrelled) {
      const second = randomElement(surnamePool.filter((s) => s.name !== surname), rng);
      if (second) {
        family.push(second);
        surname += '-' + second.name;
      }
    }
    if (gender === 'female' && family.length) surname = family.map((f) => feminineOf(f.list, f.name)).join('-');
    const given = [first];
    for (let i = 1; i < Math.min(3, Math.max(1, options.forenameCount)); i++) {
      const next = middleName(pools.get(gender) ?? [], given, rng);
      if (next === null) break;
      given.push(next);
    }
    const name: GeneratedName = { forenames: given, surname, gender, initialsOnly: options.initialsOnly };
    const full = fullName(name);
    if (!seen.has(full)) { seen.add(full); names.push(name); }
  }

  let note: string | null = null;
  if (names.length < target) {
    note = !names.length ? 'No names fit these choices.'
      : names.length === 1 ? 'Only one name fits these choices.' : `Only ${names.length} different names fit these choices.`;
  } else if (alliterationMissed) {
    note = "Some names don't alliterate: the lists have no forename to match their surname.";
  }
  return { names, note };
}

/** A forename from `pool` not already given: a few random draws, then (a small pool) the ones left. */
function middleName(pool: readonly string[], given: readonly string[], rng: RandomSource): string | null {
  for (let i = 0; i < 8; i++) {
    const pick = randomElement(pool, rng);
    if (pick === null) return null;
    if (!given.includes(pick)) return pick;
  }
  return randomElement(pool.filter((n) => !given.includes(n)), rng);
}
